import { orderWizardSteps, type SavedWizardStep } from '@pz/scene';
import type { OptionDef } from './types.js';

/** A wizard screen: one or more related questions asked together. */
export interface WizardStep {
  title?: string;
  options: OptionDef[];
  /** The last screen, "Završi" (finish.ts): the date, the promise, how many — no questions. */
  finish?: boolean;
  /** The first screen: the product's variation (variant-step.ts) — no questions. */
  variant?: boolean;
}

/** Which questions each screen asks, in order: equal keys draw the same screens. */
export function wizardStepsKey(steps: readonly WizardStep[]): string {
  return steps.map((step) => step.options.map((o) => o.id).join(',')).join('|');
}

/**
 * The wizard's screens from the product's saved grouping (`display.steps`).
 *
 * The saved screens say which questions share a screen; the definition's own
 * option order — the option set as published — says in what order they come
 * (`orderWizardSteps`). A question no saved screen names still has to be asked
 * somewhere: on its own screen, or — after a divider the saved screens do not
 * name either (an add-on's "Zvuk" heading) — with the other such questions
 * under that heading, which titles the screen. Hidden questions (conditions,
 * a retype line asked inside a song card) are left out, and a screen left
 * with nothing to ask is dropped.
 */
export function savedWizardSteps(
  all: readonly OptionDef[],
  visible: readonly OptionDef[],
  saved: readonly SavedWizardStep[],
): WizardStep[] {
  const byId = new Map(all.map((o) => [o.id, o] as const));
  const shown = new Set(visible.map((o) => o.id));
  const steps: WizardStep[] = [];
  let titled: WizardStep | undefined;
  for (const screen of orderWizardSteps(all.map((o) => o.id), saved)) {
    const options = screen.optionIds
      .map((id) => byId.get(id))
      .filter((o): o is OptionDef => Boolean(o && shown.has(o.id)));
    if (screen.saved) {
      titled = undefined;
      steps.push({ ...(screen.title ? { title: screen.title } : {}), options });
      continue;
    }
    const option = options[0];
    if (!option) continue;
    if (option.type === 'divider') {
      titled = { title: option.label, options: [] };
      steps.push(titled);
      continue;
    }
    if (titled) titled.options.push(option);
    else steps.push({ options: [option] });
  }
  return steps.filter((step) => step.options.length > 0);
}
